import React, { useState } from 'react';
import { Users, UserPlus, Calendar, BarChart3, FileText, Zap, Settings, PlusCircle, Clock, CheckCircle, AlertTriangle, Building } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';



const AdminHomeDashboard = () => {
  // Mock API calls (will throw errors showing "Not implemented")
  const [isLoading, setIsLoading] = React.useState(false);

  const handleCreateEmployee = () => {
    // TODO: Navigate to create employee form
    console.log('Navigate to create employee');
  };

  const handleCreateJob = () => {
    // TODO: Navigate to create job posting
    console.log('Navigate to create job posting');
  };

  const handleViewReports = () => {
    // TODO: Navigate to reports/analytics
    console.log('Navigate to analytics');
  };

  const handleViewSettings = () => {
    // TODO: Navigate to settings
    console.log('Navigate to settings');
  };

  // Configuration banner for backend setup
  const ConfigBanner = () => (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-5 w-5 text-blue-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-blue-700">
            <strong>Backend not configured.</strong> Set NEXT_PUBLIC_API_URL and wire endpoints to enable data.
          </p>
        </div>
      </div>
    </div>
  );

  // Metrics cards for People Snapshot
  const MetricsCard = ({ icon: IconComponent, label, value, subtitle, color = "blue" }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center">
        <div className={`p-2 rounded-md bg-${color}-100`}>
          <IconComponent className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  // Action queue items (empty state)
  const ActionQueueCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Queue</h3>
      <EmptyState
        icon={CheckCircle}
        title="No pending approvals"
        description="All PTO requests, compensation changes, and offers have been reviewed."
        primaryAction={{
          label: "View All Requests",
          onClick: () => console.log('Navigate to requests'),
          icon: FileText
        }}
      />
    </div>
  );

  // Open roles section (empty state)  
  const OpenRolesCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Open Roles</h3>
        <span className="text-sm text-gray-500">ATS Integration</span>
      </div>
      <EmptyState
        icon={Users}
        title="No open positions"
        description="Create your first job posting to start tracking candidates and managing the hiring pipeline."
        primaryAction={{
          label: "Create Job",
          onClick: handleCreateJob,
          icon: PlusCircle
        }}
      />
    </div>
  );

  // Upcoming reviews (empty state)
  const UpcomingReviewsCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming 1:1s & Reviews</h3>
      <EmptyState
        icon={Calendar}
        title="No scheduled reviews"
        description="Set up performance review cycles and 1:1 meetings to track employee development."
        primaryAction={{
          label: "Schedule Reviews",
          onClick: () => console.log('Navigate to performance'),
          icon: Clock
        }}
      />
    </div>
  );

  // Policy acknowledgments (empty state)
  const PolicyAcksCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Policy Acknowledgments</h3>
      <EmptyState
        icon={FileText}
        title="No pending acknowledgments"
        description="Upload company policies and track employee acknowledgments for compliance."
        primaryAction={{
          label: "Manage Policies",
          onClick: () => console.log('Navigate to policies'),
          icon: FileText
        }}
      />
    </div>
  );

  // Automation alerts (empty state)
  const AutomationAlertsCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation Alerts</h3>
      <EmptyState
        icon={Zap}
        title="No automation rules"
        description="Create automated workflows to streamline HR processes and reduce manual tasks."
        primaryAction={{
          label: "Create Rules",
          onClick: () => console.log('Navigate to automation'),
          icon: Zap
        }}
      />
    </div>
  );

  const pageActions = [
    {
      label: "Add Employee",
      onClick: handleCreateEmployee,
      icon: UserPlus,
      variant: "default"
    },
    {
      label: "View Reports",
      onClick: handleViewReports,
      icon: BarChart3,
      variant: "outline"
    },
    {
      label: "Settings", 
      onClick: handleViewSettings,
      icon: Settings,
      variant: "outline"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Admin Dashboard"
        subtitle="Comprehensive organizational insights and HR management"
        actions={pageActions}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ConfigBanner />
        
        {/* People Snapshot Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            icon={Users}
            label="Total Employees"
            value="0"
            subtitle="Connect backend to load data"
            color="blue"
          />
          <MetricsCard
            icon={UserPlus}
            label="Open Roles"
            value="0"
            subtitle="Create job postings"
            color="green"
          />
          <MetricsCard
            icon={Clock}
            label="Pending Onboardings"
            value="0"
            subtitle="New hires to process"
            color="yellow"
          />
          <MetricsCard
            icon={Calendar}
            label="Time-off Requests"
            value="0"
            subtitle="Awaiting approval"
            color="purple"
          />
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <ActionQueueCard />
          <OpenRolesCard />
          <UpcomingReviewsCard />
          <PolicyAcksCard />
          <AutomationAlertsCard />
          
          {/* Organization Overview */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization</h3>
            <EmptyState
              icon={Building}
              title="Setup organization"
              description="Configure departments, locations, and organizational structure."
              primaryAction={{
                label: "Organization Settings",
                onClick: () => console.log('Navigate to org settings'),
                icon: Building
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHomeDashboard;